import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  XCircle,
  User,
} from 'lucide-react';
import type { Employee } from '../types';
import { CIVILITE_MAP } from '../types';

interface EmployeeCardProps {
  employee: Employee;
}

export function EmployeeCard({ employee }: EmployeeCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { folderUuid } = useParams();

  const civilityLabel = employee.civilite
    ? CIVILITE_MAP[employee.civilite]
    : undefined;
  const lastName = employee.nom ?? employee.nomNaissance ?? '';
  const firstName = employee.prenom ?? '';
  const fullName = [
    civilityLabel,
    [firstName, lastName].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(' ');
  const matricule = employee.matriculeSalarie ?? '—';
  const email = employee.email ?? '—';
  const phone = employee.telephone ?? null;
  const birthDate = employee.dateNaissance
    ? new Date(employee.dateNaissance).toLocaleDateString('fr-FR')
    : t('common.notAvailable', 'Not available');
  const documentsCount = employee.metadata?.totalDocuments ?? 0;
  const bulletinsCount = employee.metadata?.totalBulletins ?? 0;
  const hasActiveContract = employee.isActive ?? false;
  const isActive = employee.isActive ?? false;
  console.log({employee})

  const handleClick = () => {
    navigate(`/folders/${folderUuid}/employees/${employee.uuid}`);
  };

  return (
    <div
      onClick={handleClick}
      className="group hover:border-primary-300 cursor-pointer rounded-xl border border-neutral-200 bg-white p-6 transition-all duration-200 hover:scale-102 hover:shadow-lg"
    >
      {/* Header avec avatar et statut */}
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center gap-3">
          {/* Avatar avec initiales */}
          <div className="bg-primary-100 text-primary-700 flex h-12 w-12 items-center justify-center rounded-full">
            <User className="h-6 w-6" />
          </div>

          {/* Nom et matricule */}
          <div>
            <h3 className="group-hover:text-primary-700 font-semibold text-neutral-900">
              {fullName || t('employees.unknownName', 'Name not provided')}
            </h3>
            <p className="text-sm text-neutral-500">
              {t('employees.matricule', 'Matricule')}: {matricule}
            </p>
          </div>
        </div>

        {/* Statut actif/inactif */}
        <div className="flex items-center gap-2">
          {isActive ? (
            <>
              <CheckCircle className="text-success-500 h-5 w-5" />
              <span className="text-success-700 text-xs font-medium">
                {t('common.active', 'Actif')}
              </span>
            </>
          ) : (
            <>
              <XCircle className="text-error-500 h-5 w-5" />
              <span className="text-error-700 text-xs font-medium">
                {t('common.inactive', 'Inactif')}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Informations de contact */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <Mail className="h-4 w-4 text-neutral-400" />
          <span className="truncate">{email}</span>
        </div>

        {phone && (
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <Phone className="h-4 w-4 text-neutral-400" />
            <span>{phone}</span>
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <Calendar className="h-4 w-4 text-neutral-400" />
          <span>
            {t('employees.birthDate', 'Né(e) le')} {birthDate}
          </span>
        </div>
      </div>

      {/* Footer - Statistiques */}
      <div className="mt-4 flex items-center justify-between border-t border-neutral-100 pt-4">
        <div className="text-center">
          <p className="text-lg font-semibold text-neutral-900">
            {documentsCount}
          </p>
          <p className="text-xs text-neutral-500">
            {t('employees.documents', 'Documents')}
          </p>
        </div>

        <div className="text-center">
          <p className="text-lg font-semibold text-neutral-900">
            {bulletinsCount}
          </p>
          <p className="text-xs text-neutral-500">
            {t('employees.bulletins.title', 'Payslips')}
          </p>
        </div>

        {hasActiveContract && (
          <div className="bg-success-100 rounded-full px-3 py-1">
            <p className="text-success-700 text-xs font-medium">
              {t('employees.activeContract', 'En contrat')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
